import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth } from "../../firebase";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

const CREATE_COMPANY_ENDPOINT =
  process.env.REACT_APP_CREATE_COMPANY_ENDPOINT ||
  "https://asia-east1-chat3test-59045.cloudfunctions.net/createCompanyAccount";

const DRIVER_PLANS = [
  {
    planId: "driver_1_3",
    label: "1～3 位成員",
    maxDrivers: 3,
    billableSeats: 2,
  },
  { planId: "driver_5", label: "5 位成員", maxDrivers: 5, billableSeats: 4 },
  { planId: "driver_10", label: "10 位成員", maxDrivers: 10, billableSeats: 8 },
  {
    planId: "driver_15",
    label: "15 位成員",
    maxDrivers: 15,
    billableSeats: 12,
  },
  {
    planId: "driver_20",
    label: "20 位成員",
    maxDrivers: 20,
    billableSeats: 16,
  },
  {
    planId: "driver_25",
    label: "25 位成員",
    maxDrivers: 25,
    billableSeats: 20,
  },
  {
    planId: "driver_30",
    label: "30 位成員",
    maxDrivers: 30,
    billableSeats: 24,
  },
];

const EMPTY_ADMIN = {
  displayName: "",
  email: "",
  phone: "",
  idNumber: "",
  password: "",
  confirmPassword: "",
};

function clean(value) {
  return String(value ?? "").trim();
}

function responseUid(payload) {
  return clean(
    payload?.uid ||
      payload?.data?.uid ||
      payload?.result?.uid ||
      payload?.user?.uid ||
      payload?.data?.user?.uid ||
      payload?.result?.user?.uid,
  );
}

function responseKeys(payload) {
  const keys =
    payload && typeof payload === "object" ? Object.keys(payload) : [];
  return keys.length ? keys.join(", ") : "沒有欄位";
}

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(value));
}

function todayInput() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateOnly(value) {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);

  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDateOnly(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function addDays(date, amount) {
  const next = new Date(date);
  next.setDate(next.getDate() + Number(amount || 0));
  return next;
}

function getPaidPeriodEnd(startValue, billingCycle) {
  const start = parseDateOnly(startValue);
  if (!start) return null;

  const end = new Date(start);

  if (billingCycle === "monthly") {
    end.setMonth(end.getMonth() + 1);
  } else {
    end.setFullYear(end.getFullYear() + 1);
  }

  end.setDate(end.getDate() - 1);
  return end;
}

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getContractPreview(contract) {
  const selectedPlan =
    DRIVER_PLANS.find((item) => item.planId === contract.driverPlanId) ||
    DRIVER_PLANS[2];

  const maxDeputyAdmins = Math.max(
    0,
    Math.floor(numberValue(contract.maxDeputyAdmins)),
  );
  const adminSeatUnitPrice = Math.max(
    0,
    numberValue(contract.adminSeatUnitPrice),
  );
  const driverSeatUnitPrice = Math.max(
    0,
    numberValue(contract.driverSeatUnitPrice),
  );
  const discount = Math.max(0, numberValue(contract.discount));
  const gracePeriodDays = Math.max(
    0,
    Math.floor(numberValue(contract.gracePeriodDays)),
  );

  const ownerSeats = 1;
  const adminSeats = ownerSeats + maxDeputyAdmins;
  const billingMultiplier = contract.billingCycle === "yearly" ? 12 : 1;
  const monthlyAdminFee = adminSeats * adminSeatUnitPrice;
  const monthlyDriverFee = selectedPlan.billableSeats * driverSeatUnitPrice;
  const monthlySubtotal = monthlyAdminFee + monthlyDriverFee;
  const adminFee = monthlyAdminFee * billingMultiplier;
  const driverFee = monthlyDriverFee * billingMultiplier;
  const subtotal = adminFee + driverFee;
  const total = Math.max(0, subtotal - discount);

  const paidPeriodEnd = getPaidPeriodEnd(
    contract.periodStart,
    contract.billingCycle,
  );
  const serviceEnd = paidPeriodEnd
    ? addDays(
        paidPeriodEnd,
        Math.floor(numberValue(contract.complimentaryDays)),
      )
    : null;
  const renewalReminder = serviceEnd ? addDays(serviceEnd, -45) : null;
  const gracePeriodEnd = serviceEnd
    ? addDays(serviceEnd, gracePeriodDays)
    : null;

  return {
    selectedPlan,
    ownerSeats,
    adminSeats,
    billingMultiplier,
    monthlyAdminFee,
    monthlyDriverFee,
    monthlySubtotal,
    adminFee,
    driverFee,
    subtotal,
    total,
    paidPeriodEnd: paidPeriodEnd ? formatDateOnly(paidPeriodEnd) : "",
    serviceEnd: serviceEnd ? formatDateOnly(serviceEnd) : "",
    renewalReminder: renewalReminder ? formatDateOnly(renewalReminder) : "",
    gracePeriodEnd: gracePeriodEnd ? formatDateOnly(gracePeriodEnd) : "",
  };
}

function initialCompany(account) {
  return {
    name: clean(account?.orgName || account?.orgProfile?.orgName),
    taxId: clean(account?.orgTaxId || account?.orgProfile?.orgTaxId),
    address: clean(account?.orgAddress || account?.orgProfile?.orgAddress),
    contactEmail: clean(
      account?.orgContactEmail || account?.orgProfile?.contactEmail,
    ),
    phone: clean(account?.orgPhone),
  };
}

function initialContract() {
  return {
    maxDeputyAdmins: "3",
    adminSeatUnitPrice: "1000",
    driverPlanId: "driver_10",
    driverSeatUnitPrice: "800",
    billingCycle: "yearly",
    periodStart: todayInput(),
    paidAt: todayInput(),
    complimentaryDays: "0",
    gracePeriodDays: "7",
    discount: "0",
    paymentStatus: "paid",
    paymentMethod: "manual",
    transactionId: "",
    note: "",
  };
}

function Field({
  label,
  name,
  value,
  onChange,
  required = false,
  type = "text",
  placeholder = "",
  disabled = false,
  min,
  step,
}) {
  return (
    <label className="dmca-field">
      <span>
        {label}
        {required ? <b>*</b> : null}
      </span>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        min={min}
        step={step}
      />
    </label>
  );
}

function SectionTitle({ number, title, description }) {
  return (
    <div className="dmca-section-title">
      <span>{number}</span>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </div>
  );
}

function money(value) {
  return `${Math.round(numberValue(value)).toLocaleString("zh-TW")} 元`;
}

function newMemberDraft() {
  return {
    draftId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    ...EMPTY_ADMIN,
  };
}

function dateInputValue(value) {
  if (!value) return "";

  let date = value;
  if (typeof value?.toDate === "function") date = value.toDate();
  if (!(date instanceof Date)) date = new Date(date);
  if (Number.isNaN(date.getTime())) return "";

  return formatDateOnly(date);
}

function contractToForm(contractData) {
  const data = contractData || {};
  const admin = data.admin || {};
  const driver = data.driver || {};
  const amount = data.amount || {};
  const term = data.term || {};
  const payment = data.payment || {};

  return {
    maxDeputyAdmins: String(admin.maxDeputyAdmins ?? "3"),
    adminSeatUnitPrice: String(admin.seatUnitPrice ?? "0"),
    driverPlanId: clean(driver.planId) || "driver_10",
    driverSeatUnitPrice: String(driver.seatUnitPrice ?? "0"),
    billingCycle: clean(term.billingCycle) || "yearly",
    periodStart: dateInputValue(term.paidPeriodStartAt || term.serviceStartAt),
    paidAt: dateInputValue(term.paidAt),
    complimentaryDays: String(term.complimentaryDays ?? "0"),
    gracePeriodDays: String(term.gracePeriodDays ?? "7"),
    discount: String(amount.discount ?? "0"),
    paymentStatus: clean(payment.status) || "paid",
    paymentMethod: clean(payment.method) || "manual",
    transactionId: clean(payment.transactionId),
    note: clean(data.note),
  };
}

export default function DriverManagerCompanyAccounts({
  user,
  account,
  createCompanyEndpoint = CREATE_COMPANY_ENDPOINT,
}) {
  const [companyId, setCompanyId] = useState(
    clean(account?.orgId || user?.orgId),
  );
  const [company, setCompany] = useState(() => initialCompany(account));
  const [owner, setOwner] = useState(EMPTY_ADMIN);
  const [deputyDrafts, setDeputyDrafts] = useState([]);
  const [driverDrafts, setDriverDrafts] = useState([]);
  const [contract, setContract] = useState(() => initialContract());

  const [ownerUid, setOwnerUid] = useState("");
  const [activeContractId, setActiveContractId] = useState("");
  const [deputyCreatedCount, setDeputyCreatedCount] = useState(0);
  const [driverCreatedCount, setDriverCreatedCount] = useState(0);
  const [memberRecords, setMemberRecords] = useState([]);
  const [memberEdits, setMemberEdits] = useState({});
  const [editingUid, setEditingUid] = useState("");
  const [savingUid, setSavingUid] = useState("");
  const [disablingUid, setDisablingUid] = useState("");

  const [submittingCompany, setSubmittingCompany] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [submittingOwner, setSubmittingOwner] = useState(false);
  const [submittingContract, setSubmittingContract] = useState(false);
  const [submittingDeputy, setSubmittingDeputy] = useState(false);
  const [submittingDriver, setSubmittingDriver] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [organizations, setOrganizations] = useState([]);
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

  const endpoint = createCompanyEndpoint || CREATE_COMPANY_ENDPOINT;
  const preview = useMemo(() => getContractPreview(contract), [contract]);

  const companyCreated = Boolean(companyId);
  const ownerCreated = Boolean(ownerUid);
  const contractCreated = Boolean(activeContractId);
  const deputyLimit = numberValue(contract.maxDeputyAdmins);
  const driverLimit = preview.selectedPlan.maxDrivers;
  const deputyReservedCount = deputyCreatedCount + deputyDrafts.length;
  const driverReservedCount = driverCreatedCount + driverDrafts.length;

  const filteredOrganizations = useMemo(() => {
    const keyword = clean(organizationSearch).toLowerCase();

    if (!keyword) return organizations;

    return organizations.filter((item) =>
      [item.id, item.name, item.taxId, item.contactEmail]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );
  }, [organizations, organizationSearch]);

  const loadOrganizations = useCallback(async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setOrganizations([]);
      setLoadingOrganizations(false);
      return;
    }

    setLoadingOrganizations(true);

    try {
      const snapshot = await getDocs(collection(db, "orgs"));
      const items = snapshot.docs
        .map((item) => {
          const data = item.data() || {};
          return {
            id: item.id,
            name: clean(data.orgName) || "未命名公司",
            taxId: clean(data.orgTaxId),
            status: clean(data.status) || "unknown",
            activeContractId: clean(data.activeContractId),
            contactEmail: clean(data.orgContactEmail),
          };
        })
        .sort((left, right) => left.name.localeCompare(right.name, "zh-Hant"));

      setOrganizations(items);
    } catch (loadError) {
      console.error("[CompanyAccounts] 載入公司清單失敗：", loadError);
      setError(
        "無法載入公司清單，請確認 Firestore Rules 已允許系統管理員讀取 orgs。 ",
      );
    } finally {
      setLoadingOrganizations(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  async function selectExistingOrganization(organization) {
    clearMessages();
    setSelectedOrganizationId(organization.id);
    setLoadingExisting(true);

    try {
      const orgRef = doc(db, "orgs", organization.id);
      const orgSnapshot = await getDoc(orgRef);

      if (!orgSnapshot.exists()) {
        throw new Error("找不到這間公司，請重新整理公司清單。 ");
      }

      const orgData = orgSnapshot.data() || {};
      const membersSnapshot = await getDocs(
        collection(db, "orgs", organization.id, "members"),
      );
      const members = membersSnapshot.docs.map((item) => ({
        uid: item.id,
        ...(item.data() || {}),
      }));
      const memberRecordsLoaded = await Promise.all(
        members.map(async (member) => {
          if (!member.uid) return member;
          const driverSnapshot = await getDoc(doc(db, "drivers", member.uid));
          return {
            ...member,
            ...(driverSnapshot.exists() ? driverSnapshot.data() : {}),
          };
        }),
      );
      const activeMembers = memberRecordsLoaded.filter(
        (member) => clean(member.status) === "active",
      );
      const ownerMember = activeMembers.find(
        (member) => clean(member.role) === "owner",
      );
      const deputyCount = activeMembers.filter(
        (member) => clean(member.role) === "deputy",
      ).length;
      const driverCount = activeMembers.filter(
        (member) => clean(member.role) === "driver",
      ).length;

      let contractData = null;
      const activeContractId = clean(orgData.activeContractId);

      if (activeContractId) {
        const contractSnapshot = await getDoc(
          doc(db, "orgs", organization.id, "contracts", activeContractId),
        );
        if (contractSnapshot.exists()) {
          contractData = contractSnapshot.data() || {};
        }
      }

      let ownerData = {};
      if (ownerMember?.uid) {
        const ownerSnapshot = await getDoc(doc(db, "drivers", ownerMember.uid));
        if (ownerSnapshot.exists()) ownerData = ownerSnapshot.data() || {};
      }

      setCompanyId(organization.id);
      setCompany(initialCompany(orgData));
      setOwnerUid(clean(ownerMember?.uid));
      setActiveContractId(activeContractId);
      setDeputyCreatedCount(deputyCount);
      setDriverCreatedCount(driverCount);
      setMemberRecords(memberRecordsLoaded);
      setMemberEdits(
        Object.fromEntries(
          memberRecordsLoaded.map((member) => [
            member.uid,
            {
              displayName: clean(member.displayName),
              email: clean(member.email),
              phone: clean(member.phone),
              idNumber: clean(member.idNumber),
            },
          ]),
        ),
      );
      setEditingUid("");
      if (contractData) {
        const restoredContract = contractToForm(contractData);
        const restoredPlan =
          DRIVER_PLANS.find(
            (plan) => plan.planId === restoredContract.driverPlanId,
          ) || DRIVER_PLANS[2];
        setDeputyDrafts(
          deputyCount < numberValue(restoredContract.maxDeputyAdmins)
            ? [newMemberDraft()]
            : [],
        );
        setDriverDrafts(
          driverCount < restoredPlan.maxDrivers ? [newMemberDraft()] : [],
        );
      } else {
        setDeputyDrafts([]);
        setDriverDrafts([]);
      }
      setOwner({
        displayName: clean(ownerData.displayName),
        email: clean(ownerData.email),
        phone: clean(ownerData.phone),
        idNumber: clean(ownerData.idNumber),
        password: "",
        confirmPassword: "",
      });

      if (contractData) {
        setContract(contractToForm(contractData));
      } else {
        setContract(initialContract());
      }

      setSuccess(
        `已載入「${clean(orgData.orgName) || "未命名公司"}」，可以從目前進度繼續。`,
      );
    } catch (loadError) {
      console.error("[CompanyAccounts] 載入公司資料失敗：", loadError);
      setError(loadError?.message || "載入公司資料失敗。 ");
    } finally {
      setLoadingExisting(false);
    }
  }

  function startNewOrganization() {
    clearMessages();
    setSelectedOrganizationId("");
    setCompanyId("");
    setCompany(initialCompany());
    setOwner(EMPTY_ADMIN);
    setDeputyDrafts([]);
    setDriverDrafts([]);
    setContract(initialContract());
    setOwnerUid("");
    setActiveContractId("");
    setDeputyCreatedCount(0);
    setDriverCreatedCount(0);
    setMemberRecords([]);
    setMemberEdits({});
    setEditingUid("");
  }

  function clearMessages() {
    setError("");
    setSuccess("");
  }

  function updateCompany(event) {
    const { name, value } = event.target;
    setCompany((current) => ({ ...current, [name]: value }));
    clearMessages();
  }

  function updateMemberEdit(uid, name, value) {
    setMemberEdits((current) => ({
      ...current,
      [uid]: {
        ...(current[uid] || {}),
        [name]: value,
      },
    }));
    clearMessages();
  }

  async function saveCompany() {
    clearMessages();
    const validationError = validateCompany();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSavingCompany(true);
    try {
      await callAdminApi({
        action: "updateCompany",
        orgId: companyId,
        company: {
          name: clean(company.name),
          taxId: clean(company.taxId),
          address: clean(company.address),
          contactEmail: clean(company.contactEmail),
          phone: clean(company.phone),
        },
      });
      await loadOrganizations();
      setSuccess("公司資料已更新。 ");
    } catch (requestError) {
      setError(requestError?.message || "公司資料更新失敗。 ");
    } finally {
      setSavingCompany(false);
    }
  }

  async function saveMember(member) {
    const edit = memberEdits[member.uid] || {};
    clearMessages();
    setSavingUid(member.uid);

    try {
      await callAdminApi({
        action: "updateMember",
        orgId: companyId,
        uid: member.uid,
        member: edit,
      });
      setMemberRecords((current) =>
        current.map((item) =>
          item.uid === member.uid ? { ...item, ...edit } : item,
        ),
      );
      setEditingUid("");
      setSuccess(`${clean(edit.displayName)} 的資料已更新。 `);
    } catch (requestError) {
      setError(requestError?.message || "成員資料更新失敗。 ");
    } finally {
      setSavingUid("");
    }
  }

  async function disableMember(member) {
    const name = clean(member.displayName) || "這位成員";
    const confirmed = window.confirm(
      `確定要停用「${name}」嗎？\n\n停用後會保留歷史工作與資料，不會直接刪除；該帳號將無法登入。`,
    );
    if (!confirmed) return;

    clearMessages();
    setDisablingUid(member.uid);

    try {
      await callAdminApi({
        action: "disableMember",
        orgId: companyId,
        uid: member.uid,
      });
      setMemberRecords((current) =>
        current.map((item) =>
          item.uid === member.uid
            ? { ...item, status: "inactive", memberStatus: "inactive" }
            : item,
        ),
      );
      if (member.role === "deputy") {
        setDeputyCreatedCount((current) => Math.max(0, current - 1));
      }
      if (member.role === "driver") {
        setDriverCreatedCount((current) => Math.max(0, current - 1));
      }
      if (member.role === "owner") {
        setOwnerUid("");
      }
      setSuccess(`${name} 已停用；歷史資料已保留。 `);
    } catch (requestError) {
      setError(requestError?.message || "停用成員失敗。 ");
    } finally {
      setDisablingUid("");
    }
  }

  function updateOwner(event) {
    const { name, value } = event.target;
    setOwner((current) => ({ ...current, [name]: value }));
    clearMessages();
  }

  function updateMemberDraft(setter, draftId, event) {
    const { name, value } = event.target;
    setter((current) =>
      current.map((draft) =>
        draft.draftId === draftId ? { ...draft, [name]: value } : draft,
      ),
    );
    clearMessages();
  }

  function addDeputyDraft() {
    if (deputyReservedCount >= deputyLimit) {
      setError("已達合約中的副管理員上限。 ");
      return;
    }

    setDeputyDrafts((current) => [...current, newMemberDraft()]);
    clearMessages();
  }

  function addDriverDraft() {
    if (driverReservedCount >= driverLimit) {
      setError("已達合約中的成員上限。 ");
      return;
    }

    setDriverDrafts((current) => [...current, newMemberDraft()]);
    clearMessages();
  }

  function removeDeputyDraft(draftId) {
    setDeputyDrafts((current) =>
      current.filter((draft) => draft.draftId !== draftId),
    );
  }

  function removeDriverDraft(draftId) {
    setDriverDrafts((current) =>
      current.filter((draft) => draft.draftId !== draftId),
    );
  }

  function updateContract(event) {
    const { name, value } = event.target;
    setContract((current) => ({ ...current, [name]: value }));
    clearMessages();
  }

  function validateCompany() {
    if (!clean(company.name)) return "請先輸入公司名稱。";
    if (company.contactEmail && !isEmail(company.contactEmail)) {
      return "公司聯絡 Email 格式不正確。";
    }
    return "";
  }

  function validateAdmin(data, role) {
    if (!clean(data.displayName)) return "請輸入管理員姓名。";
    if (!isEmail(data.email)) return "管理員 Email 格式不正確。";
    if (clean(data.password).length < 8) {
      return "初始密碼至少需要 8 個字元。";
    }
    if (data.password !== data.confirmPassword) {
      return "兩次輸入的初始密碼不一致。";
    }
    if (role === "owner" && !companyId) {
      return "請先建立公司。";
    }
    return "";
  }

  function validateContract() {
    if (!companyId || !ownerUid) {
      return "請先完成公司與主管理員建立。";
    }
    if (!contract.periodStart) return "請選擇合約開始日期。";
    if (!contract.paidAt) return "請選擇付款日期。";
    if (numberValue(contract.maxDeputyAdmins) < 0) {
      return "副管理員上限不可小於 0。";
    }
    if (numberValue(contract.adminSeatUnitPrice) < 0) {
      return "管理員單價不可小於 0。";
    }
    if (numberValue(contract.driverSeatUnitPrice) < 0) {
      return "成員單價不可小於 0。";
    }
    if (numberValue(contract.complimentaryDays) < 0) {
      return "免費贈送天數不可小於 0。";
    }
    if (numberValue(contract.gracePeriodDays) < 0) {
      return "寬限期不可小於 0。";
    }
    if (numberValue(contract.discount) < 0) {
      return "折扣不可小於 0。";
    }
    return "";
  }

  async function callAdminApi(body) {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("目前沒有取得登入狀態，請重新登入後再試。 ");
    }

    const token = await currentUser.getIdToken(true);
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload?.message || "後端處理失敗，請稍後再試。");
    }

    return payload;
  }

  async function createCompany() {
    clearMessages();
    const validationError = validateCompany();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmittingCompany(true);

    try {
      const payload = await callAdminApi({
        action: "createCompany",
        company: {
          name: clean(company.name),
          taxId: clean(company.taxId),
          address: clean(company.address),
          contactEmail: clean(company.contactEmail),
          phone: clean(company.phone),
        },
      });

      const nextOrgId = clean(payload.orgId || payload.company?.orgId);

      if (!nextOrgId) {
        throw new Error("公司已建立，但後端沒有回傳 orgId。 ");
      }

      setCompanyId(nextOrgId);
      setSelectedOrganizationId(nextOrgId);
      await loadOrganizations();
      setSuccess(`公司建立成功，orgId：${nextOrgId}`);
    } catch (requestError) {
      setError(requestError?.message || "公司建立失敗。 ");
    } finally {
      setSubmittingCompany(false);
    }
  }

  async function createOwner(event) {
    event.preventDefault();
    clearMessages();

    const validationError = validateAdmin(owner, "owner");
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmittingOwner(true);

    try {
      const payload = await callAdminApi({
        action: "createAdmin",
        orgId: companyId,
        admin: {
          role: "owner",
          displayName: clean(owner.displayName),
          email: clean(owner.email).toLowerCase(),
          phone: clean(owner.phone),
          idNumber: clean(owner.idNumber).toUpperCase(),
          password: owner.password,
        },
      });

      const nextUid = responseUid(payload);

      if (!nextUid) {
        throw new Error(
          `主管理員已回應，但回應中沒有 UID（收到欄位：${responseKeys(payload)}）。請部署最新的 createCompanyAccount Function。`,
        );
      }

      setOwnerUid(nextUid);
      const ownerRecord = {
        uid: nextUid,
        role: "owner",
        status: "active",
        displayName: clean(owner.displayName),
        email: clean(owner.email).toLowerCase(),
        phone: clean(owner.phone),
        idNumber: clean(owner.idNumber).toUpperCase(),
      };
      setMemberRecords((current) => [
        ...current.filter((item) => item.uid !== nextUid),
        ownerRecord,
      ]);
      setMemberEdits((current) => ({
        ...current,
        [nextUid]: {
          displayName: ownerRecord.displayName,
          email: ownerRecord.email,
          phone: ownerRecord.phone,
          idNumber: ownerRecord.idNumber,
        },
      }));
      await loadOrganizations();
      setSuccess("主管理員建立成功，現在請填寫合約內容。 ");
    } catch (requestError) {
      setError(requestError?.message || "主管理員建立失敗。 ");
    } finally {
      setSubmittingOwner(false);
    }
  }

  async function createContract(event) {
    event.preventDefault();
    clearMessages();

    const validationError = validateContract();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmittingContract(true);

    try {
      const payload = await callAdminApi({
        action: "createContract",
        orgId: companyId,
        contract: {
          contractStatus: "active",
          admin: {
            maxDeputyAdmins: Math.floor(numberValue(contract.maxDeputyAdmins)),
            seatUnitPrice: numberValue(contract.adminSeatUnitPrice),
            billingMultiplier: preview.billingMultiplier,
            monthlyFee: preview.monthlyAdminFee,
            fee: preview.adminFee,
          },
          driver: {
            planId: preview.selectedPlan.planId,
            maxDrivers: preview.selectedPlan.maxDrivers,
            billableSeats: preview.selectedPlan.billableSeats,
            seatUnitPrice: numberValue(contract.driverSeatUnitPrice),
            billingMultiplier: preview.billingMultiplier,
            monthlyFee: preview.monthlyDriverFee,
            fee: preview.driverFee,
          },
          amount: {
            currency: "TWD",
            billingMultiplier: preview.billingMultiplier,
            monthlySubtotal: preview.monthlySubtotal,
            discount: numberValue(contract.discount),
            total: preview.total,
          },
          term: {
            billingCycle: contract.billingCycle,
            paidAt: contract.paidAt,
            periodStartAt: contract.periodStart,
            paidPeriodStartAt: contract.periodStart,
            paidPeriodEndAt: preview.paidPeriodEnd,
            complimentaryDays: Math.floor(
              numberValue(contract.complimentaryDays),
            ),
            serviceStartAt: contract.periodStart,
            serviceEndAt: preview.serviceEnd,
            renewalReminderAt: preview.renewalReminder,
            gracePeriodDays: Math.floor(numberValue(contract.gracePeriodDays)),
            gracePeriodEndAt: preview.gracePeriodEnd,
          },
          payment: {
            status: contract.paymentStatus,
            method: contract.paymentMethod,
            transactionId: clean(contract.transactionId),
          },
          note: clean(contract.note),
        },
      });

      const nextContractId = clean(
        payload.contractId || payload.id || payload.contract?.contractId,
      );

      if (!nextContractId) {
        throw new Error("合約已回應，但後端沒有回傳 contractId。 ");
      }

      setActiveContractId(nextContractId);
      setDeputyDrafts(
        numberValue(contract.maxDeputyAdmins) > 0 ? [newMemberDraft()] : [],
      );
      setDriverDrafts(
        preview.selectedPlan.maxDrivers > 0 ? [newMemberDraft()] : [],
      );
      await loadOrganizations();
      setSuccess(
        `合約建立成功，contractId：${nextContractId}。現在可以新增副管理員。`,
      );
    } catch (requestError) {
      setError(requestError?.message || "合約建立失敗。 ");
    } finally {
      setSubmittingContract(false);
    }
  }

  async function createDeputy(event, draft) {
    event.preventDefault();
    clearMessages();

    const validationError = validateAdmin(draft, "deputy");
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!contractCreated) {
      setError("請先建立並啟用合約。 ");
      return;
    }

    if (deputyCreatedCount >= deputyLimit) {
      setError("已達合約中的副管理員上限。 ");
      return;
    }

    setSubmittingDeputy(true);

    try {
      const payload = await callAdminApi({
        action: "createAdmin",
        orgId: companyId,
        contractId: activeContractId,
        admin: {
          role: "deputy",
          displayName: clean(draft.displayName),
          email: clean(draft.email).toLowerCase(),
          phone: clean(draft.phone),
          idNumber: clean(draft.idNumber).toUpperCase(),
          password: draft.password,
        },
      });

      const nextUid = responseUid(payload);
      if (!nextUid) {
        throw new Error(
          `副管理員已回應，但回應中沒有 UID（收到欄位：${responseKeys(payload)}）。請部署最新的 createCompanyAccount Function。`,
        );
      }

      setDeputyCreatedCount((current) => current + 1);
      await loadOrganizations();
      const deputyRecord = {
        uid: nextUid,
        role: "deputy",
        status: "active",
        displayName: clean(draft.displayName),
        email: clean(draft.email).toLowerCase(),
        phone: clean(draft.phone),
        idNumber: clean(draft.idNumber).toUpperCase(),
      };
      setMemberRecords((current) => [...current, deputyRecord]);
      setMemberEdits((current) => ({
        ...current,
        [deputyRecord.uid]: deputyRecord,
      }));
      setDeputyDrafts((current) => {
        const remaining = current.filter(
          (item) => item.draftId !== draft.draftId,
        );
        return remaining.length === 0 && deputyCreatedCount + 1 < deputyLimit
          ? [newMemberDraft()]
          : remaining;
      });
      setSuccess(`副管理員建立成功：${clean(payload.email || draft.email)}`);
    } catch (requestError) {
      setError(requestError?.message || "副管理員建立失敗。 ");
    } finally {
      setSubmittingDeputy(false);
    }
  }

  async function createDriver(event, draft) {
    event.preventDefault();
    clearMessages();

    const validationError = validateAdmin(draft, "driver");
    if (validationError) {
      setError(validationError.replace("管理員", "成員"));
      return;
    }

    if (!contractCreated) {
      setError("請先建立並啟用合約。 ");
      return;
    }

    const maxDrivers = preview.selectedPlan.maxDrivers;
    if (driverCreatedCount >= maxDrivers) {
      setError("已達合約中的成員上限。 ");
      return;
    }

    setSubmittingDriver(true);

    try {
      const payload = await callAdminApi({
        action: "createAdmin",
        orgId: companyId,
        contractId: activeContractId,
        admin: {
          role: "driver",
          displayName: clean(draft.displayName),
          email: clean(draft.email).toLowerCase(),
          phone: clean(draft.phone),
          idNumber: clean(draft.idNumber).toUpperCase(),
          password: draft.password,
        },
      });

      const nextUid = responseUid(payload);
      if (!nextUid) {
        throw new Error(
          `成員已回應，但回應中沒有 UID（收到欄位：${responseKeys(payload)}）。請部署最新的 createCompanyAccount Function。`,
        );
      }

      setDriverCreatedCount((current) => current + 1);
      await loadOrganizations();
      const driverRecord = {
        uid: nextUid,
        role: "driver",
        status: "active",
        displayName: clean(draft.displayName),
        email: clean(draft.email).toLowerCase(),
        phone: clean(draft.phone),
        idNumber: clean(draft.idNumber).toUpperCase(),
      };
      setMemberRecords((current) => [...current, driverRecord]);
      setMemberEdits((current) => ({
        ...current,
        [driverRecord.uid]: driverRecord,
      }));
      setDriverDrafts((current) => {
        const remaining = current.filter(
          (item) => item.draftId !== draft.draftId,
        );
        return remaining.length === 0 && driverCreatedCount + 1 < driverLimit
          ? [newMemberDraft()]
          : remaining;
      });
      setSuccess(`成員建立成功：${clean(payload.email || draft.email)}`);
    } catch (requestError) {
      setError(requestError?.message || "成員建立失敗。 ");
    } finally {
      setSubmittingDriver(false);
    }
  }

  return (
    <div className="dmca-root">
      <style>{STYLES}</style>

      <header className="dmca-header">
        <div>
          <h1>公司帳號與合約</h1>
          <p>先建立公司與主管理員，再建立合約並啟用公司的使用上限。</p>
        </div>
        <div className={`dmca-status ${contractCreated ? "is-active" : ""}`}>
          <span />
          {contractCreated
            ? "合約已啟用"
            : companyCreated
              ? "建立中"
              : "等待開始"}
        </div>
      </header>

      <section className="dmca-company-picker">
        <div className="dmca-company-picker-head">
          <div>
            <strong>繼續處理既有公司</strong>
            <span>畫面重整或中斷後，可搜尋公司名稱並還原目前進度。</span>
          </div>
          <button
            type="button"
            className="dmca-picker-new"
            onClick={startNewOrganization}
            disabled={loadingExisting}
          >
            ＋ 建立新公司
          </button>
        </div>

        <div className="dmca-company-picker-controls">
          <input
            value={organizationSearch}
            onChange={(event) => setOrganizationSearch(event.target.value)}
            placeholder="搜尋公司名稱、統一編號或公司 Email"
            aria-label="搜尋公司"
          />
          <button
            type="button"
            className="dmca-picker-refresh"
            onClick={loadOrganizations}
            disabled={loadingOrganizations || loadingExisting}
          >
            {loadingOrganizations ? "載入中…" : "重新整理"}
          </button>
        </div>

        <div className="dmca-company-picker-list">
          {loadingOrganizations ? (
            <span className="dmca-picker-muted">正在載入公司清單…</span>
          ) : !filteredOrganizations.length ? (
            <span className="dmca-picker-muted">
              {organizationSearch
                ? "找不到符合的公司。"
                : "目前沒有可選取的公司。"}
            </span>
          ) : (
            filteredOrganizations.map((organization) => (
              <button
                key={organization.id}
                type="button"
                className={`dmca-company-option ${
                  selectedOrganizationId === organization.id ? "selected" : ""
                }`}
                onClick={() => selectExistingOrganization(organization)}
                disabled={loadingExisting}
              >
                <span>
                  <strong>{organization.name}</strong>
                  <small>
                    {organization.id}
                    {organization.taxId ? ` ・ ${organization.taxId}` : ""}
                  </small>
                </span>
                <em>{organization.status}</em>
              </button>
            ))
          )}
        </div>

        {loadingExisting ? (
          <div className="dmca-picker-loading">
            正在還原公司、成員與合約資料…
          </div>
        ) : null}
      </section>

      <div className="dmca-steps">
        <Step number="01" label="公司資料" active={companyCreated} />
        <Step number="02" label="主管理員" active={ownerCreated} />
        <Step number="03" label="合約設定" active={contractCreated} />
        <Step number="04" label="副管理員" active={deputyCreatedCount > 0} />
      </div>

      <main className="dmca-layout">
        <div className="dmca-main">
          <section className="dmca-card">
            <SectionTitle
              number="01"
              title="建立公司"
              description="建立後取得唯一 orgId，之後所有成員與合約都會綁定這個 orgId。"
            />

            {companyCreated ? (
              <div className="dmca-confirmed">
                <div>
                  <small>目前公司 orgId</small>
                  <code>{companyId}</code>
                </div>
                <strong>已建立</strong>
              </div>
            ) : null}

            <div className="dmca-grid">
              <Field
                label="公司名稱"
                name="name"
                value={company.name}
                onChange={updateCompany}
                required
                disabled={submittingCompany}
                placeholder="例如：交通股份有限公司"
              />
              <Field
                label="統一編號"
                name="taxId"
                value={company.taxId}
                onChange={updateCompany}
                disabled={submittingCompany}
                placeholder="可稍後補上"
              />
              <Field
                label="公司聯絡 Email"
                name="contactEmail"
                type="email"
                value={company.contactEmail}
                onChange={updateCompany}
                disabled={submittingCompany}
                placeholder="contact@example.com"
              />
              <Field
                label="公司電話"
                name="phone"
                value={company.phone}
                onChange={updateCompany}
                disabled={submittingCompany}
                placeholder="07-1234567"
              />
              <label className="dmca-field dmca-wide">
                <span>公司地址</span>
                <textarea
                  name="address"
                  value={company.address}
                  onChange={updateCompany}
                  disabled={submittingCompany}
                  placeholder="例如：高雄市苓雅區"
                  rows={2}
                />
              </label>
            </div>

            <div className="dmca-footer">
              <span>
                {companyCreated
                  ? "公司已建立，請繼續建立主管理員。"
                  : "建立後會取得 orgId。"}
              </span>
              <button
                type="button"
                className="dmca-button secondary"
                onClick={companyCreated ? saveCompany : createCompany}
                disabled={submittingCompany || savingCompany}
              >
                {submittingCompany || savingCompany
                  ? "儲存中…"
                  : companyCreated
                    ? "儲存公司資料"
                    : "建立公司並取得 orgId"}
              </button>
            </div>
          </section>

          {companyCreated ? (
            <section className="dmca-card">
              <SectionTitle
                number="02"
                title="建立主管理員"
                description="每家公司固定一位主管理員；主管理員同時可以執行成員工作。"
              />

              {ownerCreated ? (
                <div className="dmca-confirmed">
                  <div>
                    <small>主管理員 Firebase UID</small>
                    <code>{ownerUid}</code>
                    {ownerUid ? <small>UID：{ownerUid}</small> : null}
                  </div>
                  <strong>已建立</strong>
                </div>
              ) : (
                <form onSubmit={createOwner}>
                  <div className="dmca-grid">
                    <Field
                      label="姓名"
                      name="displayName"
                      value={owner.displayName}
                      onChange={updateOwner}
                      required
                      placeholder="例如：張庭瑋"
                    />
                    <Field
                      label="登入 Email"
                      name="email"
                      type="email"
                      value={owner.email}
                      onChange={updateOwner}
                      required
                      placeholder="admin@example.com"
                    />
                    <Field
                      label="手機"
                      name="phone"
                      value={owner.phone}
                      onChange={updateOwner}
                      placeholder="0912345678"
                    />
                    <Field
                      label="身分證字號"
                      name="idNumber"
                      value={owner.idNumber}
                      onChange={updateOwner}
                      placeholder="可稍後補上"
                    />
                    <Field
                      label="初始密碼"
                      name="password"
                      type="password"
                      value={owner.password}
                      onChange={updateOwner}
                      required
                      placeholder="至少 8 個字元"
                    />
                    <Field
                      label="確認初始密碼"
                      name="confirmPassword"
                      type="password"
                      value={owner.confirmPassword}
                      onChange={updateOwner}
                      required
                      placeholder="再次輸入密碼"
                    />
                  </div>
                  <div className="dmca-footer">
                    <span>角色固定為：owner</span>
                    <button
                      type="submit"
                      className="dmca-button primary"
                      disabled={submittingOwner}
                    >
                      {submittingOwner ? "建立中…" : "建立主管理員"}
                    </button>
                  </div>
                </form>
              )}
            </section>
          ) : null}

          {ownerCreated ? (
            <section className="dmca-card">
              <SectionTitle
                number="03"
                title="建立合約並啟用公司"
                description="合約資料會綁定 orgId；管理員可以開車，但不佔一般成員方案名額。"
              />

              <form onSubmit={createContract}>
                <div className="dmca-subtitle">管理員計費</div>
                <div className="dmca-grid">
                  <Field
                    label="副管理員上限"
                    name="maxDeputyAdmins"
                    type="number"
                    value={contract.maxDeputyAdmins}
                    onChange={updateContract}
                    min="0"
                    step="1"
                    required
                    disabled={contractCreated}
                  />
                  <Field
                    label="管理員單價／席位／月"
                    name="adminSeatUnitPrice"
                    type="number"
                    value={contract.adminSeatUnitPrice}
                    onChange={updateContract}
                    min="0"
                    step="1"
                    required
                    disabled={contractCreated}
                  />
                </div>
                <div className="dmca-help">
                  管理員收費席位 = 主管理員 1 位 +
                  副管理員上限。主管理員與副管理員都可以執行成員工作，但不另外計入成員方案。
                </div>

                <div className="dmca-subtitle">一般成員方案</div>
                <div className="dmca-grid">
                  <label className="dmca-field">
                    <span>
                      成員方案<b>*</b>
                    </span>
                    <select
                      name="driverPlanId"
                      value={contract.driverPlanId}
                      onChange={updateContract}
                      disabled={contractCreated}
                    >
                      {DRIVER_PLANS.map((plan) => (
                        <option key={plan.planId} value={plan.planId}>
                          {plan.label}（計費 {plan.billableSeats} 位）
                        </option>
                      ))}
                    </select>
                  </label>
                  <Field
                    label="成員單價／計費席位／月"
                    name="driverSeatUnitPrice"
                    type="number"
                    value={contract.driverSeatUnitPrice}
                    onChange={updateContract}
                    min="0"
                    step="1"
                    required
                    disabled={contractCreated}
                  />
                </div>

                <div className="dmca-subtitle">期間與付款</div>
                <div className="dmca-grid">
                  <Field
                    label="服務開始日"
                    name="periodStart"
                    type="date"
                    value={contract.periodStart}
                    onChange={updateContract}
                    required
                    disabled={contractCreated}
                  />
                  <Field
                    label="付款日"
                    name="paidAt"
                    type="date"
                    value={contract.paidAt}
                    onChange={updateContract}
                    required
                    disabled={contractCreated}
                  />
                  <label className="dmca-field">
                    <span>
                      付款週期<b>*</b>
                    </span>
                    <select
                      name="billingCycle"
                      value={contract.billingCycle}
                      onChange={updateContract}
                      disabled={contractCreated}
                    >
                      <option value="monthly">每月</option>
                      <option value="yearly">每年</option>
                    </select>
                  </label>
                  <Field
                    label="免費贈送天數"
                    name="complimentaryDays"
                    type="number"
                    value={contract.complimentaryDays}
                    onChange={updateContract}
                    min="0"
                    step="1"
                    disabled={contractCreated}
                  />
                  <Field
                    label="寬限期天數"
                    name="gracePeriodDays"
                    type="number"
                    value={contract.gracePeriodDays}
                    onChange={updateContract}
                    min="0"
                    step="1"
                    disabled={contractCreated}
                  />
                  <Field
                    label="折扣"
                    name="discount"
                    type="number"
                    value={contract.discount}
                    onChange={updateContract}
                    min="0"
                    step="1"
                    disabled={contractCreated}
                  />
                  <label className="dmca-field">
                    <span>付款狀態</span>
                    <select
                      name="paymentStatus"
                      value={contract.paymentStatus}
                      onChange={updateContract}
                      disabled={contractCreated}
                    >
                      <option value="paid">已付款</option>
                      <option value="pending">待付款</option>
                      <option value="unpaid">未付款</option>
                    </select>
                  </label>
                  <label className="dmca-field">
                    <span>付款方式</span>
                    <select
                      name="paymentMethod"
                      value={contract.paymentMethod}
                      onChange={updateContract}
                      disabled={contractCreated}
                    >
                      <option value="manual">人工登錄</option>
                      <option value="bank_transfer">銀行轉帳</option>
                      <option value="credit_card">信用卡</option>
                      <option value="ecpay">綠界</option>
                    </select>
                  </label>
                  <Field
                    label="交易編號"
                    name="transactionId"
                    value={contract.transactionId}
                    onChange={updateContract}
                    disabled={contractCreated}
                    placeholder="可選"
                  />
                  <label className="dmca-field dmca-wide">
                    <span>合約備註</span>
                    <textarea
                      name="note"
                      value={contract.note}
                      onChange={updateContract}
                      disabled={contractCreated}
                      rows={2}
                      placeholder="例如：年度合約，管理員可免費執行成員工作"
                    />
                  </label>
                </div>

                <div className="dmca-preview">
                  <div className="dmca-preview-title">合約計算預覽</div>
                  <div className="dmca-preview-grid">
                    <PreviewItem
                      label="管理員席位"
                      value={`${preview.adminSeats} 位`}
                    />
                    <PreviewItem
                      label="管理員月費"
                      value={money(preview.monthlyAdminFee)}
                    />
                    <PreviewItem
                      label="管理員本期費用"
                      value={money(preview.adminFee)}
                    />
                    <PreviewItem
                      label="可建立一般成員"
                      value={`${preview.selectedPlan.maxDrivers} 位`}
                    />
                    <PreviewItem
                      label="成員計費席位"
                      value={`${preview.selectedPlan.billableSeats} 位`}
                    />
                    <PreviewItem
                      label="成員月費"
                      value={money(preview.monthlyDriverFee)}
                    />
                    <PreviewItem
                      label="成員本期費用"
                      value={money(preview.driverFee)}
                    />
                    <PreviewItem
                      label={`本期小計（${preview.billingMultiplier} 個月）`}
                      value={money(preview.subtotal)}
                    />
                    <PreviewItem
                      label="實際服務到期日"
                      value={preview.serviceEnd || "請先選日期"}
                    />
                    <PreviewItem
                      label="45 天提醒日"
                      value={preview.renewalReminder || "請先選日期"}
                    />
                    <PreviewItem
                      label="寬限期結束日"
                      value={preview.gracePeriodEnd || "請先選日期"}
                    />
                    <PreviewItem
                      label="本期應收總額"
                      value={money(preview.total)}
                      strong
                    />
                  </div>
                </div>

                <div className="dmca-footer">
                  <span>
                    {contractCreated
                      ? `目前合約：${activeContractId}`
                      : "確認後會建立合約文件並啟用公司。"}
                  </span>
                  <button
                    type="submit"
                    className="dmca-button primary"
                    disabled={contractCreated || submittingContract}
                  >
                    {submittingContract
                      ? "建立中…"
                      : contractCreated
                        ? "合約已啟用"
                        : "建立合約並啟用公司"}
                  </button>
                </div>
              </form>
            </section>
          ) : null}

          {companyCreated && memberRecords.length > 0 ? (
            <section className="dmca-card">
              <SectionTitle
                number="04"
                title="已建立成員"
                description="已建立的帳號會保留在列表中；編輯會更新資料，停用會保留歷史紀錄。"
              />
              <div className="dmca-member-list">
                {memberRecords.map((member) => {
                  const edit = memberEdits[member.uid] || {};
                  const isEditing = editingUid === member.uid;
                  const isInactive = clean(member.status) !== "active";
                  const roleLabel =
                    member.role === "owner"
                      ? "主管理員"
                      : member.role === "deputy"
                        ? "副管理員"
                        : "成員";

                  return (
                    <div
                      key={member.uid}
                      className={`dmca-member-row ${isInactive ? "inactive" : ""}`}
                    >
                      <div className="dmca-member-main">
                        <div className="dmca-member-avatar">
                          {clean(edit.displayName).slice(0, 1) || "?"}
                        </div>
                        <div className="dmca-member-info">
                          <strong>
                            {clean(edit.displayName) || "未命名成員"}
                          </strong>
                          <span>
                            {roleLabel} · {isInactive ? "已停用" : "使用中"}
                          </span>
                          <small>{clean(edit.email) || "沒有 Email"}</small>
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="dmca-member-edit-grid">
                          <input
                            value={edit.displayName || ""}
                            onChange={(event) =>
                              updateMemberEdit(
                                member.uid,
                                "displayName",
                                event.target.value,
                              )
                            }
                            placeholder="姓名"
                          />
                          <input
                            value={edit.email || ""}
                            onChange={(event) =>
                              updateMemberEdit(
                                member.uid,
                                "email",
                                event.target.value,
                              )
                            }
                            placeholder="Email"
                            type="email"
                          />
                          <input
                            value={edit.phone || ""}
                            onChange={(event) =>
                              updateMemberEdit(
                                member.uid,
                                "phone",
                                event.target.value,
                              )
                            }
                            placeholder="手機"
                          />
                          <input
                            value={edit.idNumber || ""}
                            onChange={(event) =>
                              updateMemberEdit(
                                member.uid,
                                "idNumber",
                                event.target.value,
                              )
                            }
                            placeholder="身分證字號"
                          />
                        </div>
                      ) : null}

                      <div className="dmca-member-actions">
                        {isEditing ? (
                          <>
                            <button
                              type="button"
                              className="dmca-small-button dark"
                              onClick={() => saveMember(member)}
                              disabled={savingUid === member.uid}
                            >
                              {savingUid === member.uid ? "儲存中" : "儲存"}
                            </button>
                            <button
                              type="button"
                              className="dmca-small-button"
                              onClick={() => setEditingUid("")}
                            >
                              取消
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="dmca-small-button"
                            onClick={() => setEditingUid(member.uid)}
                          >
                            編輯
                          </button>
                        )}
                        {!isInactive ? (
                          <button
                            type="button"
                            className="dmca-small-button danger"
                            onClick={() => disableMember(member)}
                            disabled={disablingUid === member.uid}
                          >
                            {disablingUid === member.uid ? "處理中" : "停用"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          {contractCreated && deputyLimit > 0 ? (
            <section className="dmca-card">
              <div className="dmca-member-heading">
                <SectionTitle
                  number="04"
                  title="副管理員"
                  description="先按新增預留席位，再逐筆填寫並建立帳號。"
                />
                <button
                  type="button"
                  className="dmca-add-button"
                  onClick={addDeputyDraft}
                  disabled={deputyReservedCount >= deputyLimit}
                >
                  ＋ 新增副管理員
                </button>
              </div>

              <div className="dmca-limit-bar">
                <span>副管理員席位（已建立＋已預留）</span>
                <strong>
                  {deputyReservedCount} / {deputyLimit}
                </strong>
              </div>

              {deputyDrafts.map((draft, index) => (
                <form
                  key={draft.draftId}
                  className="dmca-draft-card"
                  onSubmit={(event) => createDeputy(event, draft)}
                >
                  <div className="dmca-draft-head">
                    <strong>副管理員 {deputyCreatedCount + index + 1}</strong>
                    <button
                      type="button"
                      className="dmca-draft-remove"
                      onClick={() => removeDeputyDraft(draft.draftId)}
                    >
                      移除預留
                    </button>
                  </div>
                  <div className="dmca-grid">
                    <Field
                      label="姓名"
                      name="displayName"
                      value={draft.displayName}
                      onChange={(event) =>
                        updateMemberDraft(setDeputyDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="例如：李小華"
                    />
                    <Field
                      label="登入 Email"
                      name="email"
                      type="email"
                      value={draft.email}
                      onChange={(event) =>
                        updateMemberDraft(setDeputyDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="deputy@example.com"
                    />
                    <Field
                      label="手機"
                      name="phone"
                      value={draft.phone}
                      onChange={(event) =>
                        updateMemberDraft(setDeputyDrafts, draft.draftId, event)
                      }
                      placeholder="可重複，僅作聯絡資料"
                    />
                    <Field
                      label="身分證字號"
                      name="idNumber"
                      value={draft.idNumber}
                      onChange={(event) =>
                        updateMemberDraft(setDeputyDrafts, draft.draftId, event)
                      }
                      placeholder="可稍後補上"
                    />
                    <Field
                      label="初始密碼"
                      name="password"
                      type="password"
                      value={draft.password}
                      onChange={(event) =>
                        updateMemberDraft(setDeputyDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="至少 8 個字元"
                    />
                    <Field
                      label="確認初始密碼"
                      name="confirmPassword"
                      type="password"
                      value={draft.confirmPassword}
                      onChange={(event) =>
                        updateMemberDraft(setDeputyDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="再次輸入密碼"
                    />
                  </div>
                  <div className="dmca-footer">
                    <span>此列尚未寫入 Firebase。</span>
                    <button
                      type="submit"
                      className="dmca-button primary"
                      disabled={submittingDeputy}
                    >
                      {submittingDeputy ? "建立中…" : "建立此副管理員"}
                    </button>
                  </div>
                </form>
              ))}

              {!deputyDrafts.length ? (
                <div className="dmca-member-collapsed">
                  目前沒有待填寫的副管理員；按右上角新增即可增加一列。
                </div>
              ) : null}
              {deputyReservedCount >= deputyLimit ? (
                <div className="dmca-limit-reached">已達副管理員可用上限。</div>
              ) : null}
            </section>
          ) : null}

          {contractCreated && driverLimit > 0 ? (
            <section className="dmca-card">
              <div className="dmca-member-heading">
                <SectionTitle
                  number="05"
                  title="一般成員"
                  description="先按新增預留席位，再逐筆填寫並建立帳號。"
                />
                <button
                  type="button"
                  className="dmca-add-button"
                  onClick={addDriverDraft}
                  disabled={driverReservedCount >= driverLimit}
                >
                  ＋ 新增成員
                </button>
              </div>

              <div className="dmca-limit-bar">
                <span>成員席位（已建立＋已預留）</span>
                <strong>
                  {driverReservedCount} / {driverLimit}
                </strong>
              </div>

              {driverDrafts.map((draft, index) => (
                <form
                  key={draft.draftId}
                  className="dmca-draft-card"
                  onSubmit={(event) => createDriver(event, draft)}
                >
                  <div className="dmca-draft-head">
                    <strong>成員 {driverCreatedCount + index + 1}</strong>
                    <button
                      type="button"
                      className="dmca-draft-remove"
                      onClick={() => removeDriverDraft(draft.draftId)}
                    >
                      移除預留
                    </button>
                  </div>
                  <div className="dmca-grid">
                    <Field
                      label="姓名"
                      name="displayName"
                      value={draft.displayName}
                      onChange={(event) =>
                        updateMemberDraft(setDriverDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="例如：王大明"
                    />
                    <Field
                      label="登入 Email"
                      name="email"
                      type="email"
                      value={draft.email}
                      onChange={(event) =>
                        updateMemberDraft(setDriverDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="driver@example.com"
                    />
                    <Field
                      label="手機"
                      name="phone"
                      value={draft.phone}
                      onChange={(event) =>
                        updateMemberDraft(setDriverDrafts, draft.draftId, event)
                      }
                      placeholder="可重複，僅作聯絡資料"
                    />
                    <Field
                      label="身分證字號"
                      name="idNumber"
                      value={draft.idNumber}
                      onChange={(event) =>
                        updateMemberDraft(setDriverDrafts, draft.draftId, event)
                      }
                      placeholder="可稍後補上"
                    />
                    <Field
                      label="初始密碼"
                      name="password"
                      type="password"
                      value={draft.password}
                      onChange={(event) =>
                        updateMemberDraft(setDriverDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="至少 8 個字元"
                    />
                    <Field
                      label="確認初始密碼"
                      name="confirmPassword"
                      type="password"
                      value={draft.confirmPassword}
                      onChange={(event) =>
                        updateMemberDraft(setDriverDrafts, draft.draftId, event)
                      }
                      required
                      placeholder="再次輸入密碼"
                    />
                  </div>
                  <div className="dmca-footer">
                    <span>此列尚未寫入 Firebase。</span>
                    <button
                      type="submit"
                      className="dmca-button primary"
                      disabled={submittingDriver}
                    >
                      {submittingDriver ? "建立中…" : "建立此成員"}
                    </button>
                  </div>
                </form>
              ))}

              {!driverDrafts.length ? (
                <div className="dmca-member-collapsed">
                  目前沒有待填寫的成員；按右上角新增即可增加一列。
                </div>
              ) : null}
              {driverReservedCount >= driverLimit ? (
                <div className="dmca-limit-reached">已達成員方案可用上限。</div>
              ) : null}
            </section>
          ) : null}

          {error ? (
            <div className="dmca-alert error" role="alert">
              <strong>處理失敗</strong>
              <span>{error}</span>
            </div>
          ) : null}

          {success ? (
            <div className="dmca-alert success" role="status">
              <strong>處理成功</strong>
              <span>{success}</span>
            </div>
          ) : null}
        </div>

        <aside className="dmca-side">
          <div className="dmca-side-mark">ORG</div>
          <h2>建立順序</h2>
          <ol>
            <li className={companyCreated ? "done" : ""}>
              建立公司並取得 orgId
            </li>
            <li className={ownerCreated ? "done" : ""}>建立主管理員帳號</li>
            <li className={contractCreated ? "done" : ""}>
              建立合約並啟用公司
            </li>
            <li className={deputyCreatedCount > 0 ? "done" : ""}>
              新增副管理員
            </li>
          </ol>

          <div className="dmca-side-note">
            <strong>成員會在哪裡建立？</strong>
            <span>
              一般成員使用既有的 DriverManagerDrivers.js；建立時會使用同一個
              orgId，角色固定為 driver。
            </span>
          </div>

          <div className="dmca-side-note pale">
            <strong>方案 A</strong>
            <span>
              owner 與 deputy
              都可以執行成員工作，但只計入管理員費用，不佔一般成員方案名額。
            </span>
          </div>
        </aside>
      </main>
    </div>
  );
}

function Step({ number, label, active }) {
  return (
    <div className={`dmca-step ${active ? "active" : ""}`}>
      <span>{number}</span>
      <b>{label}</b>
    </div>
  );
}

function PreviewItem({ label, value, strong = false }) {
  return (
    <div className={`dmca-preview-item ${strong ? "strong" : ""}`}>
      <small>{label}</small>
      <b>{value}</b>
    </div>
  );
}

const STYLES = `
.dmca-root{height:auto;min-height:100%;box-sizing:border-box;overflow:visible;padding:30px;color:#25313b;background:#f5f7fb;font-family:Inter,"Noto Sans TC",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
.dmca-header{max-width:1180px;margin:0 auto 16px;display:flex;align-items:flex-end;justify-content:space-between;gap:20px}
.dmca-eyebrow{color:#e87820;font-size:10px;font-weight:900;letter-spacing:2px}
.dmca-header h1{margin:7px 0 5px;color:#18232d;font-size:29px;line-height:1.15}
.dmca-header p{margin:0;color:#87939d;font-size:12px}
.dmca-status{display:flex;align-items:center;gap:8px;padding:9px 12px;border:1px solid #eadfbd;border-radius:10px;background:#fffdf5;color:#9a7b34;font-size:11px;font-weight:800;white-space:nowrap}
.dmca-status span{width:7px;height:7px;border-radius:50%;background:#e4b83e}
.dmca-status.is-active{border-color:#c9ead6;background:#f3fcf6;color:#31824f}
.dmca-status.is-active span{background:#2eb866}
.dmca-company-picker{max-width:1180px;margin:0 auto 16px;padding:15px;border:1px solid #e1e8ef;border-radius:15px;background:#fff;box-shadow:0 8px 22px rgba(24,39,52,.04)}
.dmca-company-picker-head{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-bottom:11px}
.dmca-company-picker-head>div{display:grid;gap:4px}
.dmca-company-picker-head strong{color:#25313b;font-size:13px}
.dmca-company-picker-head span{color:#8a969f;font-size:10px}
.dmca-picker-new,.dmca-picker-refresh{min-height:34px;padding:0 12px;border-radius:8px;font-size:10px;font-weight:900;cursor:pointer;white-space:nowrap}
.dmca-picker-new{border:1px solid #efb178;background:#fffaf5;color:#c86b27}
.dmca-picker-refresh{border:1px solid #dce5ed;background:#f8fafc;color:#54718c}
.dmca-picker-new:disabled,.dmca-picker-refresh:disabled{cursor:not-allowed;opacity:.55}
.dmca-company-picker-controls{display:flex;gap:8px}
.dmca-company-picker-controls input{flex:1;min-width:0;height:38px;padding:0 11px;border:1px solid #e1e6ea;border-radius:9px;background:#fbfcfd;color:#2b3742;font:inherit;font-size:11px;outline:0}
.dmca-company-picker-controls input:focus{border-color:#f2a15f;background:#fff;box-shadow:0 0 0 3px #fff2e6}
.dmca-company-picker-list{display:flex;gap:8px;margin-top:10px;overflow-x:auto;padding-bottom:2px}
.dmca-company-option{display:flex;align-items:center;justify-content:space-between;gap:18px;min-width:225px;padding:9px 11px;border:1px solid #e4e9ed;border-radius:9px;background:#fbfcfd;color:#52606b;text-align:left;cursor:pointer}
.dmca-company-option:hover,.dmca-company-option.selected{border-color:#f0ad70;background:#fff9f3}
.dmca-company-option:disabled{cursor:wait;opacity:.65}
.dmca-company-option>span{display:grid;gap:4px;min-width:0}
.dmca-company-option strong{overflow:hidden;color:#344452;font-size:11px;text-overflow:ellipsis;white-space:nowrap}
.dmca-company-option small{overflow:hidden;color:#99a4ac;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9px;text-overflow:ellipsis;white-space:nowrap}
.dmca-company-option em{padding:3px 6px;border-radius:999px;background:#eef7f1;color:#3d875b;font-size:9px;font-style:normal;font-weight:900}
.dmca-picker-muted{padding:8px 2px;color:#98a2aa;font-size:10px}
.dmca-picker-loading{margin-top:9px;color:#c86b27;font-size:10px;font-weight:800}
.dmca-steps{max-width:1180px;margin:0 auto 16px;display:grid;grid-template-columns:repeat(4,1fr);gap:9px}
.dmca-step{display:flex;align-items:center;gap:8px;padding:10px 12px;border:1px solid #e4e9ed;border-radius:11px;background:#fff;color:#9aa4ac;font-size:11px;font-weight:800}
.dmca-step span{display:grid;place-items:center;width:25px;height:25px;border-radius:8px;background:#f1f3f5;color:#9aa4ac;font-size:9px}
.dmca-step.active{border-color:#cdebd8;color:#328052;background:#f8fdf9}
.dmca-step.active span{background:#dcf5e5;color:#2c9256}
.dmca-layout{max-width:1180px;margin:0 auto;display:grid;grid-template-columns:minmax(0,1fr) 250px;align-items:start;gap:16px}
.dmca-main{display:grid;gap:16px;min-width:0}
.dmca-card,.dmca-side{border:1px solid #e5e9ed;border-radius:17px;background:#fff;box-shadow:0 8px 22px rgba(24,39,52,.045)}
.dmca-card{padding:24px}
.dmca-section-title{display:flex;align-items:flex-start;gap:11px;margin-bottom:18px}
.dmca-section-title>span{display:grid;place-items:center;width:30px;height:30px;border-radius:9px;background:#fff1e6;color:#d87528;font-size:10px;font-weight:900}
.dmca-section-title h2{margin:1px 0 4px;color:#1c2934;font-size:17px}
.dmca-section-title p{margin:0;color:#8b969e;font-size:11px;line-height:1.5}
.dmca-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:13px}
.dmca-field{display:grid;gap:6px;min-width:0}
.dmca-field>span{color:#52606b;font-size:11px;font-weight:800}
.dmca-field>span b{margin-left:3px;color:#e45e50}
.dmca-field input,.dmca-field textarea,.dmca-field select{width:100%;box-sizing:border-box;border:1px solid #e1e6ea;border-radius:9px;outline:0;background:#fbfcfd;color:#2b3742;font:inherit;font-size:12px;line-height:1.5}
.dmca-field input,.dmca-field select{height:42px;padding:0 12px}
.dmca-field textarea{min-height:68px;padding:10px 12px;resize:vertical}
.dmca-field input:focus,.dmca-field textarea:focus,.dmca-field select:focus{border-color:#f2a15f;background:#fff;box-shadow:0 0 0 3px #fff2e6}
.dmca-field input:disabled,.dmca-field textarea:disabled,.dmca-field select:disabled{cursor:not-allowed;background:#f1f3f5;color:#818c95}
.dmca-wide{grid-column:1/-1}
.dmca-confirmed{display:flex;align-items:center;justify-content:space-between;gap:14px;margin:-4px 0 16px;padding:11px 13px;border:1px solid #ccebd8;border-radius:10px;background:#f3fcf6;color:#31784d}
.dmca-confirmed div{display:grid;gap:4px;min-width:0}
.dmca-confirmed small{color:#4c8c63;font-size:10px;font-weight:800}
.dmca-confirmed code{overflow:hidden;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;text-overflow:ellipsis;white-space:nowrap}
.dmca-confirmed strong{font-size:11px;white-space:nowrap}
.dmca-footer{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:19px;padding-top:17px;border-top:1px solid #edf0f2;color:#929da5;font-size:10px}
.dmca-button{min-height:40px;padding:0 16px;border-radius:9px;font-size:11px;font-weight:900;cursor:pointer;white-space:nowrap}
.dmca-button.primary{border:0;background:linear-gradient(135deg,#ff9937,#f27d20);box-shadow:0 7px 14px rgba(242,125,32,.18);color:#fff}
.dmca-button.secondary{border:1px solid #efb178;background:#fffaf5;color:#c86b27}
.dmca-button:hover:not(:disabled){filter:brightness(1.03);transform:translateY(-1px)}
.dmca-button:disabled{cursor:not-allowed;opacity:.55;transform:none}
.dmca-subtitle{margin:20px 0 11px;padding-bottom:8px;border-bottom:1px solid #edf0f2;color:#283642;font-size:12px;font-weight:900}
.dmca-help{margin-top:10px;padding:10px 12px;border-radius:9px;background:#f7faff;color:#7290b0;font-size:10px;line-height:1.6}
.dmca-preview{margin-top:20px;padding:15px;border:1px solid #e6edf5;border-radius:12px;background:#fbfdff}
.dmca-preview-title{margin-bottom:12px;color:#285d8f;font-size:12px;font-weight:900}
.dmca-preview-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}
.dmca-preview-item{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:9px 10px;border-radius:8px;background:#fff;border:1px solid #edf1f5}
.dmca-preview-item small{color:#8996a1;font-size:10px}
.dmca-preview-item b{color:#34495b;font-size:11px;text-align:right}
.dmca-preview-item.strong{border-color:#f3c18e;background:#fffaf5}
.dmca-preview-item.strong b{color:#c76722;font-size:13px}
.dmca-limit-bar{display:flex;align-items:center;justify-content:space-between;margin:-4px 0 16px;padding:11px 13px;border-radius:9px;background:#f7faff;color:#6483a3;font-size:11px;font-weight:800}
.dmca-limit-bar strong{color:#2d6597;font-size:13px}
.dmca-limit-reached{padding:16px;border:1px dashed #d9dee3;border-radius:10px;background:#fafbfc;color:#89949c;font-size:12px;text-align:center}
.dmca-alert{display:grid;gap:4px;padding:12px 14px;border-radius:10px;font-size:11px}
.dmca-alert strong{font-size:12px}
.dmca-alert.error{border:1px solid #f5d2ce;background:#fff5f3;color:#a63e36}
.dmca-alert.success{border:1px solid #ccebd8;background:#f3fcf6;color:#31784d}
.dmca-side{position:sticky;top:18px;padding:21px}
.dmca-side-mark{display:grid;place-items:center;width:38px;height:38px;margin-bottom:13px;border-radius:11px;background:#fff1e6;color:#d87528;font-size:10px;font-weight:900;letter-spacing:1px}
.dmca-side h2{margin:0 0 14px;color:#25313b;font-size:15px}
.dmca-side ol{display:grid;gap:12px;margin:0;padding:0 0 0 20px;color:#89949c;font-size:11px;line-height:1.5}
.dmca-side li.done{color:#31824f;font-weight:800}
.dmca-side-note{display:grid;gap:5px;margin-top:22px;padding:11px;border-radius:9px;background:#fff9ed;color:#9b7840;font-size:10px;line-height:1.55}
.dmca-side-note strong{color:#976d29;font-size:11px}
.dmca-side-note.pale{background:#f7faff;color:#7290b0}
.dmca-side-note.pale strong{color:#356c9d}
@media(max-width:900px){.dmca-root{padding:20px 14px}.dmca-header{align-items:flex-start;flex-direction:column}.dmca-company-picker-head{align-items:flex-start;flex-direction:column}.dmca-layout{grid-template-columns:1fr}.dmca-side{position:static}.dmca-card{padding:18px}}
@media(max-width:620px){.dmca-steps{grid-template-columns:repeat(2,1fr)}.dmca-grid,.dmca-preview-grid{grid-template-columns:1fr}.dmca-wide{grid-column:auto}.dmca-footer{align-items:stretch;flex-direction:column}.dmca-button{width:100%}.dmca-confirmed{align-items:flex-start;flex-direction:column;gap:7px}}

/* Tallarge / Apple visual layer */
.dmca-root{height:auto;min-height:100%;box-sizing:border-box;overflow:visible;padding:34px 38px 52px;background:#f5f5f7;color:#1d1d1f;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","SF Pro Text","Noto Sans TC",sans-serif;letter-spacing:.01em}
.dmca-header{max-width:1220px;margin:0 auto 24px;align-items:center}
.dmca-eyebrow{color:#8b8b91;font-size:10px;font-weight:700;letter-spacing:1.8px}
.dmca-header h1{margin:8px 0 7px;color:#1d1d1f;font-size:32px;font-weight:700;letter-spacing:-.04em}
.dmca-header p{color:#6e6e73;font-size:13px;line-height:1.5}
.dmca-status{padding:8px 12px;border:1px solid #dedee2;border-radius:999px;background:rgba(255,255,255,.75);color:#6e6e73;font-size:11px;font-weight:600;box-shadow:0 1px 2px rgba(0,0,0,.03)}
.dmca-status span{width:6px;height:6px;background:#ff9f0a}
.dmca-status.is-active{border-color:#b7e3c5;background:#f2fbf5;color:#258443}
.dmca-status.is-active span{background:#34c759}
.dmca-company-picker{max-width:1220px;margin:0 auto 18px;padding:18px 20px;border:1px solid #e4e4e8;border-radius:14px;background:rgba(255,255,255,.88);box-shadow:0 3px 14px rgba(0,0,0,.035)}
.dmca-company-picker-head{margin-bottom:14px}
.dmca-company-picker-head strong{color:#1d1d1f;font-size:14px;font-weight:650}
.dmca-company-picker-head span{color:#86868b;font-size:11px}
.dmca-picker-new,.dmca-picker-refresh{min-height:34px;border-radius:8px;font-size:11px;font-weight:600;transition:background .15s ease,border-color .15s ease}
.dmca-picker-new{border:1px solid #d8d8dc;background:#fff;color:#1d1d1f}
.dmca-picker-new:hover{border-color:#b9b9be;background:#f5f5f7}
.dmca-picker-refresh{border:1px solid #e1e1e5;background:#f5f5f7;color:#5f6066}
.dmca-picker-refresh:hover{background:#e9e9ed}
.dmca-company-picker-controls input{height:40px;border-color:#d8d8dc;border-radius:8px;background:#f7f7f8;color:#1d1d1f;font-size:12px}
.dmca-company-picker-controls input:focus{border-color:#8e8e93;background:#fff;box-shadow:0 0 0 3px rgba(0,0,0,.06)}
.dmca-company-picker-list{margin-top:12px}
.dmca-company-option{min-width:238px;padding:10px 12px;border-color:#e5e5e8;border-radius:9px;background:#fbfbfc}
.dmca-company-option:hover,.dmca-company-option.selected{border-color:#a7a7ad;background:#f5f5f7}
.dmca-company-option strong{color:#1d1d1f;font-size:12px;font-weight:600}
.dmca-company-option small{color:#8e8e93;font-family:inherit;font-size:9px}
.dmca-company-option em{background:#eaf7ed;color:#248a45;font-size:9px;font-weight:600}
.dmca-steps{max-width:1220px;margin:0 auto 18px;grid-template-columns:repeat(4,1fr);gap:1px;border:1px solid #e1e1e5;border-radius:11px;background:#e1e1e5;overflow:hidden}
.dmca-step{min-height:48px;padding:9px 14px;border:0;border-radius:0;background:#fff;color:#8e8e93;font-size:11px;font-weight:600}
.dmca-step span{width:24px;height:24px;border-radius:50%;background:#f1f1f3;color:#8e8e93;font-size:9px}
.dmca-step.active{background:#fbfbfc;color:#1d1d1f}
.dmca-step.active span{background:#1d1d1f;color:#fff}
.dmca-layout{max-width:1220px;grid-template-columns:minmax(0,1fr) 270px;gap:20px}
.dmca-main{gap:18px}
.dmca-card,.dmca-side{border:1px solid #e3e3e7;border-radius:14px;background:rgba(255,255,255,.96);box-shadow:0 4px 18px rgba(0,0,0,.035)}
.dmca-card{padding:27px}
.dmca-member-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:16px}
.dmca-member-heading .dmca-section-title{flex:1;min-width:0}
.dmca-add-button{min-height:36px;margin-top:1px;padding:0 12px;border:1px solid #d1d1d6;border-radius:8px;background:#fff;color:#1d1d1f;font-size:10px;font-weight:650;cursor:pointer;white-space:nowrap}
.dmca-add-button:hover:not(:disabled){background:#f5f5f7;border-color:#a7a7ad}
.dmca-add-button:disabled{cursor:not-allowed;opacity:.5}
.dmca-draft-card{margin-top:12px;padding:16px;border:1px solid #e4e4e8;border-radius:11px;background:#fbfbfc}
.dmca-draft-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:13px;color:#1d1d1f;font-size:12px}
.dmca-draft-remove{border:0;background:transparent;color:#b3261e;font-size:10px;font-weight:600;cursor:pointer}
.dmca-draft-remove:hover{text-decoration:underline}
.dmca-member-list{display:grid;gap:9px}
.dmca-member-row{display:grid;grid-template-columns:minmax(170px,1fr) minmax(260px,1.7fr) auto;align-items:center;gap:14px;padding:12px;border:1px solid #e5e5e8;border-radius:10px;background:#fbfbfc}
.dmca-member-row.inactive{opacity:.62;background:#f5f5f7}
.dmca-member-main{display:flex;align-items:center;gap:10px;min-width:0}
.dmca-member-avatar{display:grid;place-items:center;flex:0 0 32px;width:32px;height:32px;border-radius:50%;background:#1d1d1f;color:#fff;font-size:12px;font-weight:700}
.dmca-member-info{display:grid;gap:3px;min-width:0}
.dmca-member-info strong{overflow:hidden;color:#1d1d1f;font-size:12px;text-overflow:ellipsis;white-space:nowrap}
.dmca-member-info span{color:#6e6e73;font-size:10px}
.dmca-member-info small{overflow:hidden;color:#8e8e93;font-size:10px;text-overflow:ellipsis;white-space:nowrap}
.dmca-member-edit-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:7px}
.dmca-member-edit-grid input{width:100%;box-sizing:border-box;height:34px;padding:0 9px;border:1px solid #d8d8dc;border-radius:7px;background:#fff;color:#1d1d1f;font-size:10px;outline:0}
.dmca-member-edit-grid input:focus{border-color:#8e8e93;box-shadow:0 0 0 3px rgba(0,0,0,.05)}
.dmca-member-actions{display:flex;justify-content:flex-end;gap:6px;flex-wrap:wrap}
.dmca-small-button{min-height:30px;padding:0 9px;border:1px solid #d1d1d6;border-radius:7px;background:#fff;color:#4b4b50;font-size:10px;font-weight:600;cursor:pointer;white-space:nowrap}
.dmca-small-button:hover:not(:disabled){background:#f0f0f2}
.dmca-small-button.dark{border-color:#1d1d1f;background:#1d1d1f;color:#fff}
.dmca-small-button.danger{border-color:#efc5c1;color:#b3261e}
.dmca-small-button:disabled{cursor:not-allowed;opacity:.55}
.dmca-member-collapsed{display:flex;align-items:center;justify-content:space-between;min-height:48px;padding:0 12px;border:1px dashed #d8d8dc;border-radius:9px;background:#fbfbfc;color:#86868b;font-size:11px}
.dmca-section-title{gap:12px;margin-bottom:22px}
.dmca-section-title>span{width:28px;height:28px;border-radius:8px;background:#1d1d1f;color:#fff;font-size:9px}
.dmca-section-title h2{color:#1d1d1f;font-size:18px;font-weight:650;letter-spacing:-.02em}
.dmca-section-title p{color:#86868b;font-size:11px}
.dmca-grid{gap:15px}
.dmca-field{gap:7px}
.dmca-field>span{color:#3c3c43;font-size:11px;font-weight:600}
.dmca-field input,.dmca-field textarea,.dmca-field select{border-color:#d8d8dc;border-radius:8px;background:#fbfbfc;color:#1d1d1f;font-size:12px}
.dmca-field input,.dmca-field select{height:43px}
.dmca-field input:focus,.dmca-field textarea:focus,.dmca-field select:focus{border-color:#8e8e93;background:#fff;box-shadow:0 0 0 3px rgba(0,0,0,.055)}
.dmca-confirmed{border-color:#c9e8d2;border-radius:9px;background:#f4fbf6;color:#248a45}
.dmca-footer{margin-top:22px;padding-top:18px;color:#86868b}
.dmca-button{min-height:42px;border-radius:8px;font-size:11px;font-weight:650;transition:transform .15s ease,filter .15s ease}
.dmca-button.primary{background:#1d1d1f;box-shadow:0 5px 12px rgba(0,0,0,.13);color:#fff}
.dmca-button.secondary{border-color:#d1d1d6;background:#fff;color:#1d1d1f}
.dmca-button:hover:not(:disabled){filter:brightness(1.08);transform:translateY(-1px)}
.dmca-subtitle{margin:24px 0 13px;padding-bottom:9px;border-color:#ededf0;color:#1d1d1f;font-size:12px;font-weight:650}
.dmca-help{border-radius:8px;background:#f5f5f7;color:#6e6e73;font-size:10px}
.dmca-preview{border-color:#e0e0e5;border-radius:11px;background:#f8f8fa}
.dmca-preview-title{color:#1d1d1f;font-size:12px;font-weight:650}
.dmca-preview-item{border-color:#e8e8ec;background:#fff}
.dmca-preview-item small{color:#86868b}
.dmca-preview-item b{color:#1d1d1f;font-weight:600}
.dmca-preview-item.strong{border-color:#1d1d1f;background:#1d1d1f}
.dmca-preview-item.strong small,.dmca-preview-item.strong b{color:#fff}
.dmca-limit-bar{border-radius:8px;background:#f5f5f7;color:#6e6e73}
.dmca-limit-bar strong{color:#1d1d1f}
.dmca-alert.error{border-color:#f2c7c3;background:#fff6f5;color:#b3261e}
.dmca-alert.success{border-color:#c9e8d2;background:#f4fbf6;color:#248a45}
.dmca-side{top:20px;padding:22px}
.dmca-side-mark{width:36px;height:36px;border-radius:10px;background:#1d1d1f;color:#fff;font-size:9px}
.dmca-side h2{color:#1d1d1f;font-size:16px;font-weight:650}
.dmca-side ol{color:#86868b}
.dmca-side li.done{color:#248a45}
.dmca-side-note{background:#f5f5f7;color:#6e6e73}
.dmca-side-note strong{color:#1d1d1f}
.dmca-side-note.pale{background:#f5f5f7;color:#6e6e73}
.dmca-side-note.pale strong{color:#1d1d1f}
@media(max-width:900px){.dmca-root{padding:24px 14px 40px}.dmca-header{align-items:flex-start;flex-direction:column}.dmca-company-picker-head{align-items:flex-start;flex-direction:column}.dmca-layout{grid-template-columns:1fr}.dmca-side{position:static}.dmca-card{padding:20px}.dmca-member-heading{align-items:stretch;flex-direction:column}.dmca-add-button{align-self:flex-end}.dmca-member-row{grid-template-columns:1fr}.dmca-member-actions{justify-content:flex-start}}
.dmca-root{height:calc(100vh - 122px);max-height:calc(100vh - 122px);min-height:0;overflow-y:scroll;overflow-x:hidden;scrollbar-gutter:stable;scrollbar-width:auto}
.dmca-root::-webkit-scrollbar{width:12px}
.dmca-root::-webkit-scrollbar-track{background:#ededf0}
.dmca-root::-webkit-scrollbar-thumb{border:3px solid #ededf0;border-radius:999px;background:#a7a7ad}
.dmca-root::-webkit-scrollbar-thumb:hover{background:#85858c}
`;
